import { queryOptions, useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash-es';
import { type Group, groupScheme } from '@/models/group/group-info';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { useUserId } from '@/stores/auth';
import { splitBy } from '@/utils/array';
import { fetcher } from '@/utils/fetcher';

export const groupsQueryOptions = queryOptions({
  queryKey: ['/GetMyGroup'],
  queryFn: async ({ signal }) => {
    const response = await fetcher<ListRespondedWith<Group>>('/GetMyGroup', undefined, {
      signal,
      schema: generateListResponseScheme(groupScheme),
      validResponseCodes: [0, 1],
    });

    return response?.Data ?? [];
  },
  select: (data) => {
    const groupMap = data.reduce<Record<number, Group>>((accu, group, index) => {
      accu[group.ID] = { ...group, Order: index };
      return accu;
    }, {});

    return {
      groupMap,
      groups: sortBy(data, (g) => -g.ID),
    };
  },
});

export default function useGroups() {
  const query = useQuery(groupsQueryOptions);

  return {
    ...query,
    data: query.data?.groups ?? [],
    groups: query.data?.groupMap ?? {},
  };
}

interface CategorizedGroups {
  created: Group[];
  joined: Group[];
}

export function useCategorizedGroups() {
  const currentUserId = useUserId();

  const query = useQuery({
    ...groupsQueryOptions,
    select: (data): CategorizedGroups => {
      // const groups = sortBy(data, (g) => -g.ID);
      const [created, joined] = splitBy(data, (group) => group.CreatorID === currentUserId);

      return {
        created,
        joined,
      };
    },
  });

  return {
    createdGroups: query.data?.created ?? [],
    joinedGroups: query.data?.joined ?? [],
    ...query,
  };
}

export function useGroup(groupId: Nullable<number>) {
  const query = useQuery({
    ...groupsQueryOptions,
    select: (data) => (data && groupId ? data[groupId] : undefined),
  });

  return {
    group: query.data,
    isJoined: Boolean(query.data),
    ...query,
  };
}
